<template>
	<span v-if="params.value" class="product-cell-renderer">
		<span v-if="badge" class="product-cell-renderer__badge" :style="{ borderColor: `rgb(var(--v-theme-${badgeColor}))`, color: `rgb(var(--v-theme-${badgeColor}))` }">{{ badge }}</span>
		<v-avatar v-if="showAvatar" :size="avatarSize" rounded="lg" :color="imageUrl ? 'white' : 'surface-variant'" class="product-cell-renderer__thumb">
			<v-img v-if="imageUrl" :src="imageUrl" alt="" />
			<v-icon v-else :size="avatarSize <= 28 ? 16 : 20">{{ icon }}</v-icon>
		</v-avatar>
		<span class="product-cell-renderer__label">
			<span class="product-cell-renderer__name-row">
				<span class="product-cell-renderer__name">{{ params.value }}</span>
				<template v-for="(tag, index) in tags" :key="index">
					<v-icon v-if="!tag.text" class="product-cell-renderer__tag-icon" :class="tag.class">{{ tag.icon }}</v-icon>
					<v-chip v-else size="x-small" variant="outlined" label :class="['product-cell-renderer__tag', tag.class]">
						<v-icon v-if="tag.icon">{{ tag.icon }}</v-icon>
						{{ tag.text }}
					</v-chip>
				</template>
			</span>
			<span v-if="subtitle" class="product-cell-renderer__subtitle">{{ subtitle }}</span>
		</span>
		<v-icon v-if="showFlag" class="product-cell-renderer__flag" :title="flagTooltip">{{ flagIcon }}</v-icon>
	</span>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue';
import { getAccountImageUrl } from '@/api/core/file.js';

const props = defineProps({
	params: { type: Object, required: true },
});

const subtitle = computed(() => props.params?.subtitle || '');
const showFlag = computed(() => !!props.params?.flag);
const flagIcon = computed(() => props.params?.flagIcon || 'flag');
const flagTooltip = computed(() => props.params?.flagTooltip || '');
const icon = computed(() => props.params?.icon || 'inventory_2');
const showAvatar = computed(() => props.params?.icon !== false);
const avatarSize = computed(() => props.params?.avatarSize || 28);
const badge = computed(() => props.params?.badge ?? null);
const badgeColor = computed(() => props.params?.badgeColor || 'secondary');
const imageThumbnailUid = computed(() => props.params?.imageThumbnailUid || null);
const tags = computed(() => (Array.isArray(props.params?.tags) ? props.params.tags : []));
const imageUrl = ref(null);
watchEffect(async () => {
	const uid = imageThumbnailUid.value;
	imageUrl.value = uid ? await getAccountImageUrl(uid) : null;
});
</script>

<style lang="scss">
.product-cell-renderer {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	height: 100%;

	&__flag {
		flex: none;
		color: #c62828;
		font-size: 24px;
	}

	&__badge {
		flex: none;
		padding: 2px 8px;
		border: solid 1px;
		border-radius: 20px;
		font-size: 12px;
		font-weight: 600;
		white-space: nowrap;
	}

	&__thumb {
		flex: none;
	}

	&__label {
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		justify-content: center;
		min-width: 0;
	}

	&__flag {
		flex: none;
	}

	&__name-row {
		display: flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
	}

	&__tag,
	&__tag-icon {
		flex: none;
	}

	&__tag-icon {
		font-size: 20px;
	}

	&__name,
	&__subtitle {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		line-height: 1.3;
	}

	&__name {
		flex: 0 1 auto;
		min-width: 0;
		font-weight: 600;
	}

	&__subtitle {
		font-size: 11px;
		color: rgba(var(--v-theme-on-surface), 0.6);
	}
}
</style>
